import type {
  Action,
  GameEntity,
  GameObject,
  Parameter,
  Rule,
  Scene,
  Value,
} from "./types.ts";
import { currentState, findState, stateKey } from "./states.ts";
import { findAnimation, poseOf, sample, type Pose } from "./animation.ts";
import { descendants } from "./tree.ts";
import type { Animation, Condition } from "./types.ts";
export function parameter(
  object: GameObject,
  name: Parameter,
  entities: GameEntity[],
): number {
  const direct = object[name];
  if (direct !== null) return direct;
  const field = entities
    .find((e) => e.id === object.entityId)
    ?.properties.find((p) => p.id === object.bindings[name]);
  if (
    field?.type === "number" &&
    typeof field.value === "number" &&
    Number.isFinite(field.value)
  )
    return Math.max(0, Math.min(2000, field.value));
  return name === "speed" ? 160 : 96;
}
const targeted = [
  "show",
  "hide",
  "activate",
  "deactivate",
  "destroy",
  "text",
  "state",
  "play",
  "stop",
  "frame",
];
/** Events that happen to a layer, which actions can address as «объект события». */
export const objectEvents: Rule["event"][] = [
  "trigger",
  "pickup",
  "interact",
  "click",
  "press",
  "hover",
  "leave",
  "animationEnd",
  "stateChange",
];
/** Every layer an action applies to, without repeats. */
export const targetsOf = (a: Action) => [
  ...new Set([a.targetId, ...(a.targetIds ?? [])].filter(Boolean)),
];
// An empty name means the layer's own picture, so it always exists.
const missingState = (o: GameObject, name: Value) =>
  String(name).trim() !== "" && !findState(o, String(name));
export function sceneErrors(
  scene: Scene,
  scenes: Scene[],
  entities: GameEntity[],
): string[] {
  const out: string[] = [],
    vars = new Map(
      [
        ...scenes.flatMap((s) =>
          s.content.variables.filter((v) => v.scope === "project"),
        ),
        ...scene.content.variables,
      ].map((v) => [v.name, v]),
    ),
    objects = new Map(scene.content.objects.map((o) => [o.id, o])),
    ids = new Set(objects.keys());
  if (scene.content.objects.filter((o) => o.player).length > 1)
    out.push("В сцене может быть только один управляемый персонаж.");
  for (const o of scene.content.objects) {
    if (o.entityId && !entities.some((e) => e.id === o.entityId))
      out.push(`${o.name}: сущность удалена.`);
    for (const key of ["speed", "dashDistance"] as const)
      if (
        o.bindings[key] &&
        !entities
          .find((e) => e.id === o.entityId)
          ?.properties.some(
            (p) => p.id === o.bindings[key] && p.type === "number",
          )
      )
        out.push(`${o.name}: связанный параметр ${key} недоступен.`);
  }
  for (const r of scene.content.rules) {
    if (!r.enabled) continue;
    const eventObject = objectEvents.includes(r.event),
      detail = (r.detail ?? "").trim(),
      owner = objects.get(r.targetId);
    if (!r.actions.length) out.push(`${r.name}: добавь хотя бы одно действие.`);
    if (eventObject && r.targetId !== "$any" && !ids.has(r.targetId))
      out.push(`${r.name}: не выбран объект события.`);
    if (r.event === "variable" && detail && !vars.has(detail))
      out.push(`${r.name}: переменная «${detail}» не найдена.`);
    if (r.event === "animationEnd" && detail && owner && !findAnimation(owner, detail))
      out.push(`${r.name}: у «${owner.name}» нет анимации «${detail}».`);
    if (r.event === "stateChange" && detail && owner && !findState(owner, detail))
      out.push(`${r.name}: у «${owner.name}» нет состояния «${detail}».`);
    for (const c of r.conditions) {
      if (c.kind === "variable" && !vars.has(c.variable))
        out.push(`${r.name}: переменная «${c.variable}» не найдена.`);
      if (
        c.kind === "variable" &&
        vars.has(c.variable) &&
        ["gt", "gte", "lt", "lte"].includes(c.operator) &&
        (vars.get(c.variable)!.type !== "number" ||
          (c.compare === "variable"
            ? vars.get(c.other)?.type !== "number"
            : typeof c.value !== "number"))
      )
        out.push(`${r.name}: сравнение больше/меньше требует числа.`);
      if (c.kind === "variable" && c.compare === "variable" && !vars.has(c.other))
        out.push(`${r.name}: переменная «${c.other}» не найдена.`);
      if (c.kind !== "variable" && c.targetId === "$event" && !eventObject)
        out.push(`${r.name}: у этого события нет объекта.`);
      if (
        c.kind !== "variable" &&
        c.targetId !== "$event" &&
        !ids.has(c.targetId)
      )
        out.push(`${r.name}: объект условия не найден.`);
      if (c.kind === "state" && !["eq", "ne"].includes(c.operator))
        out.push(`${r.name}: состояние сравнивается только через = или ≠.`);
      const owner = objects.get(c.targetId);
      if (c.kind === "state" && owner && missingState(owner, c.value))
        out.push(`${r.name}: у «${owner.name}» нет состояния «${c.value}».`);
    }
    for (const a of [...r.actions, ...(r.elseActions ?? [])]) {
      const targets = targetsOf(a);
      if (
        targeted.includes(a.type) &&
        targets.includes("$event") &&
        !eventObject
      )
        out.push(`${r.name}: у этого события нет объекта.`);
      if (["set", "add", "subtract"].includes(a.type)) {
        const v = vars.get(a.variable);
        if (!v) out.push(`${r.name}: переменная «${a.variable}» не найдена.`);
        else if (
          ["add", "subtract"].includes(a.type) &&
          (v.type !== "number" || typeof a.value !== "number")
        )
          out.push(`${r.name}: арифметика требует числовую переменную.`);
        else if (
          a.type === "set" &&
          typeof a.value !== (v.type === "text" ? "string" : v.type)
        )
          out.push(`${r.name}: тип значения не совпадает с переменной.`);
      }
      if (
        targeted.includes(a.type) &&
        (!targets.length ||
          targets.some((id) => id !== "$event" && !ids.has(id)))
      )
        out.push(`${r.name}: объект действия не найден.`);
      for (const id of targets) {
        const target = objects.get(id);
        if (!target) continue;
        if (a.type === "state" && missingState(target, a.value))
          out.push(`${r.name}: у «${target.name}» нет состояния «${a.value}».`);
        if (a.type === "play" && !findAnimation(target, String(a.value)))
          out.push(`${r.name}: у «${target.name}» нет анимации «${a.value}».`);
        if (a.type === "frame" && target.kind !== "frame")
          out.push(`${r.name}: «${target.name}» не фрейм.`);
      }
      if (a.type === "scene" && !scenes.some((s) => s.id === a.sceneId))
        out.push(`${r.name}: сцена перехода не найдена.`);
    }
  }
  return [...new Set(out)];
}
const overlap = (a: GameObject, b: GameObject) =>
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y;
export class GameRuntime {
  scenes: Scene[];
  entities: GameEntity[];
  scene: Scene;
  objects: GameObject[] = [];
  globals: Record<string, Value> = {};
  locals: Record<string, Value> = {};
  log: string[] = [];
  result: "" | "win" | "lose" = "";
  message = "";
  elapsed = 0;
  cooldown = 0;
  direction = { x: 1, y: 0 };
  target: { x: number; y: number } | null = null;
  inside = new Set<string>();
  picked = new Set<string>();
  timers = new Map<string, number>();
  /** Bumped on every state change so the stage restarts the animation. */
  restarts: Record<string, number> = {};
  /** Animations in progress, by layer. */
  motions = new Map<
    string,
    { animation: Animation; start: Pose; began: number }
  >();
  private origins = new Map<string, Pose>();
  private depth = 0;
  /** Layer under the pointer and where the last press began. */
  private hovered: string | null = null;
  private pressed: string | null = null;
  constructor(scenes: Scene[], entities: GameEntity[], startId: string) {
    this.scenes = structuredClone(scenes);
    this.entities = structuredClone(entities);
    this.scene = this.scenes.find((s) => s.id === startId)!;
    if (!this.scene) throw new Error("Сцена не найдена.");
    for (const s of this.scenes)
      for (const v of s.content.variables)
        if (v.scope === "project" && this.globals[v.name] === undefined)
          this.globals[v.name] = v.value;
    this.enter(startId);
  }
  get variables() {
    return { ...this.globals, ...this.locals };
  }
  text(value: string) {
    return value.replace(/\{([\p{L}\p{N}_]+)\}/gu, (_m, key) =>
      String(this.variables[key] ?? `{${key}}`),
    );
  }
  private record(text: string) {
    this.log = [...this.log.slice(-99), text];
  }
  private enter(id: string) {
    const scene = this.scenes.find((s) => s.id === id);
    if (!scene) return;
    const errors = sceneErrors(scene, this.scenes, this.entities);
    if (errors.length) {
      this.result = "lose";
      this.message = errors[0];
      this.record(errors[0]);
      return;
    }
    this.scene = scene;
    this.objects = structuredClone(scene.content.objects);
    this.locals = {};
    for (const v of scene.content.variables)
      if (v.scope === "scene") this.locals[v.name] = v.value;
    this.inside.clear();
    this.picked.clear();
    this.timers.clear();
    this.restarts = {};
    this.motions.clear();
    this.hovered = null;
    this.pressed = null;
    this.origins = new Map(this.objects.map((o) => [o.id, poseOf(o)]));
    this.elapsed = 0;
    this.cooldown = 0;
    this.direction = { x: 1, y: 0 };
    this.target = null;
    this.record("Начало сцены: " + scene.title);
    this.emit("start");
  }
  private set(name: string, value: Value) {
    const store = Object.hasOwn(this.locals, name)
      ? this.locals
      : Object.hasOwn(this.globals, name)
        ? this.globals
        : null;
    if (!store || store[name] === value) return;
    store[name] = value;
    this.emit("variable", { detail: name });
  }
  /** Applies one action; true when the scene ended or changed. */
  private action(a: Action, eventId: string): boolean {
    if (a.type === "set") this.set(a.variable, a.value);
    if (a.type === "add" || a.type === "subtract") {
      const v = this.variables[a.variable];
      if (typeof v === "number" && typeof a.value === "number")
        this.set(a.variable, v + a.value * (a.type === "add" ? 1 : -1));
    }
    const scene = this.scene.id;
    if (targeted.includes(a.type))
      for (const target of targetsOf(a)) {
        const id = target === "$event" ? eventId : target,
          object = this.objects.find((o) => o.id === id);
        if (object) this.apply(a, object);
        if (this.result || scene !== this.scene.id) return true;
      }
    if (a.type === "win" || a.type === "lose") {
      this.result = a.type;
      this.message =
        String(a.value) || (a.type === "win" ? "Победа!" : "Поражение");
      this.record(this.message);
      return true;
    }
    if (a.type === "scene") {
      this.enter(a.sceneId);
      return true;
    }
    return !!this.result || scene !== this.scene.id;
  }
  private apply(a: Action, object: GameObject) {
    if (a.type === "show") object.visible = true;
    if (a.type === "hide") object.visible = false;
    if (a.type === "activate") object.active = true;
    if (a.type === "deactivate") object.active = false;
    if (a.type === "destroy") {
      object.active = false;
      object.visible = false;
    }
    if (a.type === "text") object.text = String(a.value);
    if (a.type === "play") {
      const animation = findAnimation(object, String(a.value));
      if (animation) {
        this.motions.set(object.id, {
          animation,
          start: poseOf(object),
          began: this.elapsed,
        });
        this.record(`Анимация: ${object.name} → ${animation.name}`);
        this.animate(object.id);
      }
    }
    if (a.type === "stop") this.motions.delete(object.id);
    if (a.type === "state") {
      const name = String(a.value).trim(),
        next = name ? findState(object, name) : null;
      if (!name || next) this.changeState(object, next?.id ?? null);
    }
    if (a.type === "frame" && object.kind === "frame") this.show(object, a.value);
  }
  private changeState(object: GameObject, state: string | null) {
    this.restarts[object.id] = (this.restarts[object.id] || 0) + 1;
    if (object.state === state) return;
    object.state = state;
    const name = currentState(object)?.name ?? "";
    this.record(`Состояние: ${object.name} → ${name || "исходное"}`);
    this.emit("stateChange", { object: object.id, detail: name });
  }
  /**
   * Shows a frame and hides the frames beside it, like a prototype page
   * turning. The frame and everything in it fade or slide in together.
   */
  private show(frame: GameObject, transition: Value) {
    for (const o of this.objects)
      if (o.kind === "frame" && o.parentId === frame.parentId && o !== frame)
        o.visible = false;
    frame.visible = true;
    this.record("Фрейм: " + frame.name);
    if (transition !== "fade" && transition !== "slide") return;
    const inside = descendants(this.objects, [frame.id]);
    for (const o of this.objects) {
      if (o !== frame && !inside.has(o.id)) continue;
      // A transition cut short ends first, so frames never drift.
      const running = this.motions.get(o.id);
      if (running?.animation.id === "$transition")
        Object.assign(
          o,
          sample(o, running.animation, running.start, running.start, Infinity)
            .pose,
        );
      const end = poseOf(o),
        animation: Animation = {
          id: "$transition",
          name: "",
          loop: false,
          steps: [
            {
              id: "$transition",
              kind: transition === "fade" ? "opacity" : "offset",
              duration: 0.35,
              easing: "easeOut",
              x: -frame.width,
              y: 0,
              value: end.opacity,
              state: "",
            },
          ],
        };
      this.motions.set(o.id, {
        animation,
        start:
          transition === "fade"
            ? { ...end, opacity: 0 }
            : { ...end, x: end.x + frame.width },
        began: this.elapsed,
      });
      this.animate(o.id);
    }
  }
  private check(c: Condition, subject: string) {
    const target =
      c.kind === "variable"
        ? undefined
        : this.objects.find(
            (o) => o.id === (c.targetId === "$event" ? subject : c.targetId),
          );
    if (c.kind === "state") {
      const same =
        !!target &&
        stateKey(currentState(target)?.name ?? "") === stateKey(String(c.value));
      return c.operator === "ne" ? !same : same;
    }
    const value =
        c.kind === "active" ? target?.active : this.variables[c.variable],
      other =
        c.kind === "variable" && c.compare === "variable"
          ? this.variables[c.other]
          : c.value;
    if (c.operator === "eq") return value === other;
    if (c.operator === "ne") return value !== other;
    if (typeof value !== "number" || typeof other !== "number") return false;
    return c.operator === "gt"
      ? value > other
      : c.operator === "gte"
        ? value >= other
        : c.operator === "lt"
          ? value < other
          : value <= other;
  }
  /**
   * Runs the rules waiting for an event. `object` is the layer it happened
   * to, `chain` the layers it counts for: a click on a picture inside a group
   * is a click on the group too.
   */
  private emit(
    event: Rule["event"],
    fire: {
      object?: string;
      chain?: string[];
      key?: string;
      detail?: string;
      rule?: string;
    } = {},
  ) {
    if (this.result) return;
    if (++this.depth > 32) {
      this.result = "lose";
      this.message = "Правила зациклились: слишком много мгновенных переходов подряд.";
      this.record(this.message);
      this.depth--;
      return;
    }
    try {
      const current = this.scene.id,
        eventId = fire.object ?? "",
        chain = fire.chain ?? (eventId ? [eventId] : []);
      for (const r of this.scene.content.rules) {
        if (
          !r.enabled ||
          r.event !== event ||
          (fire.rule && r.id !== fire.rule) ||
          (event === "key" && r.key !== fire.key)
        )
          continue;
        const detail = (r.detail ?? "").trim();
        if (
          detail &&
          ["animationEnd", "stateChange", "variable"].includes(event) &&
          (event === "variable"
            ? detail !== fire.detail
            : stateKey(detail) !== stateKey(fire.detail ?? ""))
        )
          continue;
        let subject = "";
        if (objectEvents.includes(event)) {
          if (r.targetId === "$any" && chain.includes(eventId))
            subject = eventId;
          else if (r.targetId !== "$any" && chain.includes(r.targetId))
            subject = r.targetId;
          else continue;
        }
        const valid = r.conditions.every((c) => this.check(c, subject)),
          list = valid ? r.actions : (r.elseActions ?? []);
        if (!list.length) continue;
        this.record(valid ? "Правило: " + r.name : `Правило: ${r.name} · иначе`);
        for (const a of list) if (this.action(a, subject)) return;
        if (current !== this.scene.id || this.result) return;
      }
    } finally {
      this.depth--;
    }
  }
  /** Applies the current frame of a layer's animation. */
  private animate(id: string) {
    const m = this.motions.get(id),
      o = this.objects.find((v) => v.id === id);
    if (!m || !o) return this.motions.delete(id);
    const { pose, done } = sample(
      o,
      m.animation,
      m.start,
      this.origins.get(id) ?? m.start,
      this.elapsed - m.began,
    );
    const state = pose.state !== o.state;
    if (state) this.restarts[id] = (this.restarts[id] || 0) + 1;
    Object.assign(o, pose);
    if (done) this.motions.delete(id);
    if (state)
      this.emit("stateChange", {
        object: id,
        detail: currentState(o)?.name ?? "",
      });
    if (done && m.animation.id !== "$transition")
      this.emit("animationEnd", { object: id, detail: m.animation.name });
  }
  private move(player: GameObject, dx: number, dy: number, distance: number) {
    const scene = this.scene.id,
      steps = Math.max(1, Math.ceil(distance / 4));
    for (let i = 0; i < steps; i++) {
      const solid = this.objects.filter(
        (o) => o.id !== player.id && o.active && o.solid,
      );
      const x = Math.max(
        0,
        Math.min(
          this.scene.content.width - player.width,
          player.x + (dx * distance) / steps,
        ),
      );
      if (!solid.some((o) => overlap({ ...player, x }, o))) player.x = x;
      const y = Math.max(
        0,
        Math.min(
          this.scene.content.height - player.height,
          player.y + (dy * distance) / steps,
        ),
      );
      if (!solid.some((o) => overlap({ ...player, y }, o))) player.y = y;
      this.intersections();
      if (this.result || scene !== this.scene.id || !player.active) return;
    }
  }
  keyDown(code: string) {
    if (this.result) return;
    const start = this.scene.id,
      player = this.objects.find((o) => o.player && o.active);
    this.emit("key", { key: code });
    if (this.result || start !== this.scene.id || !player) return;
    if (code === "Space" && player.dash && this.cooldown <= 0) {
      this.move(
        player,
        this.direction.x,
        this.direction.y,
        parameter(player, "dashDistance", this.entities),
      );
      this.cooldown = player.dashCooldown;
      this.target = null;
      this.intersections();
    }
    if (code === "KeyE") {
      const nearest = this.objects
        .filter((o) => o.id !== player.id && o.active && o.interactable)
        .map((o) => ({
          o,
          d: Math.hypot(
            o.x + o.width / 2 - player.x - player.width / 2,
            o.y + o.height / 2 - player.y - player.height / 2,
          ),
        }))
        .filter((v) => v.d <= player.interactionRadius)
        .sort((a, b) => a.d - b.d)[0];
      if (nearest) this.emit("interact", { object: nearest.o.id });
    }
  }
  click(x: number, y: number) {
    this.target = { x, y };
  }
  /** The layer and the groups and frames around it, innermost first. */
  private chain(id: string | null) {
    const out: string[] = [];
    for (let o = this.objects.find((v) => v.id === id); o; ) {
      out.push(o.id);
      const parent = o.parentId;
      o = parent ? this.objects.find((v) => v.id === parent) : undefined;
    }
    return out;
  }
  private live(id: string | null) {
    const o = this.objects.find((v) => v.id === id);
    return o && o.active && o.visible ? o.id : null;
  }
  /** The pointer moved onto another layer, or off every layer (null). */
  hover(id: string | null) {
    const next = this.live(id);
    if (this.result || next === this.hovered) return;
    const before = this.chain(this.hovered),
      after = this.chain(next),
      left = this.hovered,
      scene = this.scene.id;
    this.hovered = next;
    if (left)
      this.emit("leave", {
        object: left,
        chain: before.filter((v) => !after.includes(v)),
      });
    if (next && scene === this.scene.id)
      this.emit("hover", {
        object: next,
        chain: after.filter((v) => !before.includes(v)),
      });
  }
  pointerDown(id: string | null) {
    this.pressed = this.live(id);
    if (this.pressed)
      this.emit("press", {
        object: this.pressed,
        chain: this.chain(this.pressed),
      });
  }
  /** A click is a press and release on the same layer. */
  pointerUp(id: string | null) {
    const pressed = this.pressed;
    this.pressed = null;
    if (pressed && pressed === this.live(id))
      this.emit("click", { object: pressed, chain: this.chain(pressed) });
  }
  private intersections() {
    const player = this.objects.find((o) => o.player && o.active);
    if (!player) return;
    const scene = this.scene.id,
      inside = new Set<string>();
    for (const o of [...this.objects]) {
      if (o.id === player.id || !o.active || !overlap(player, o)) continue;
      if (o.pickup && !this.picked.has(o.id)) {
        this.picked.add(o.id);
        this.record("Подбор: " + o.name);
        this.emit("pickup", { object: o.id });
      }
      if (this.result || scene !== this.scene.id) return;
      if (o.trigger && o.active) {
        inside.add(o.id);
        if (!this.inside.has(o.id)) this.emit("trigger", { object: o.id });
      }
      if (this.result || scene !== this.scene.id) return;
    }
    this.inside = inside;
  }
  step(delta: number, keys: Set<string>) {
    if (this.result) return;
    const dt = Math.max(0, Math.min(0.1, delta));
    this.elapsed += dt;
    this.cooldown -= dt;
    const current = this.scene.id;
    for (const id of [...this.motions.keys()]) {
      this.animate(id);
      if (this.result || current !== this.scene.id) return;
    }
    const player = this.objects.find((o) => o.player && o.active);
    if (player) {
      let dx = 0,
        dy = 0;
      if (player.control === "wasd") {
        dx =
          Number(keys.has("KeyD") || keys.has("ArrowRight")) -
          Number(keys.has("KeyA") || keys.has("ArrowLeft"));
        dy =
          Number(keys.has("KeyS") || keys.has("ArrowDown")) -
          Number(keys.has("KeyW") || keys.has("ArrowUp"));
      } else if (this.target) {
        dx = this.target.x - player.x - player.width / 2;
        dy = this.target.y - player.y - player.height / 2;
        if (Math.hypot(dx, dy) < 4) {
          dx = 0;
          dy = 0;
          this.target = null;
        }
      }
      const length = Math.hypot(dx, dy);
      if (length) {
        this.direction = { x: dx / length, y: dy / length };
        this.move(
          player,
          this.direction.x,
          this.direction.y,
          Math.min(
            parameter(player, "speed", this.entities) * dt,
            player.control === "click" ? length : Infinity,
          ),
        );
      }
      this.intersections();
    }
    if (this.result || current !== this.scene.id) return;
    for (const r of this.scene.content.rules) {
      if (!r.enabled || r.event !== "timer") continue;
      const next = this.timers.get(r.id) ?? r.seconds;
      if (this.elapsed >= next) {
        this.timers.set(r.id, r.repeat ? next + r.seconds : Infinity);
        this.emit("timer", { rule: r.id });
        if (this.result || current !== this.scene.id) return;
      }
    }
  }
}
